/**
 * Agent-wallet session storage (#688). One entry per (env, user address) in localStorage:
 *
 *   hip4.agent.v1.<env>.<user lowercase>  →  { v, user, agentAddress, agentKey, agentName, validUntil, createdAt }
 *
 * The agent key never leaves the browser and is never logged (see docs/research/AGENT_WALLET.md for the threat
 * model). Expired or malformed entries are deleted on read. All storage access is best-effort: with storage blocked
 * the session simply does not survive a reload.
 */
import type { Address, Hex } from 'viem';
import { privateKeyToAddress } from 'viem/accounts';
import type { EnvName } from '../config/env';

export const AGENT_STORAGE_PREFIX = 'hip4.agent.v1.';

export interface AgentSession {
  v: 1;
  env: EnvName;
  /** Lowercase user (master) address the agent was approved for. */
  user: string;
  agentAddress: Address;
  /** Agent private key. Never log, never send anywhere. */
  agentKey: Hex;
  /** Name as sent in approveAgent (base + " valid_until <ms>"). */
  agentName: string;
  /** Expiry in ms since epoch (also enforced by Hyperliquid via the valid_until suffix). */
  validUntil: number;
  createdAt: number;
}

export function agentStorageKey(env: EnvName, user: string): string {
  return `${AGENT_STORAGE_PREFIX}${env}.${user.toLowerCase()}`;
}

function storage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

function valid(x: unknown, env: EnvName, user: string): x is AgentSession {
  if (!x || typeof x !== 'object') return false;
  const s = x as Partial<AgentSession>;
  if (s.v !== 1 || s.env !== env || s.user !== user.toLowerCase()) return false;
  if (typeof s.agentKey !== 'string' || !/^0x[0-9a-fA-F]{64}$/.test(s.agentKey)) return false;
  if (typeof s.agentAddress !== 'string' || typeof s.agentName !== 'string') return false;
  if (typeof s.validUntil !== 'number' || typeof s.createdAt !== 'number') return false;
  try {
    // The stored address must belong to the stored key (tampered / corrupted entries are dropped).
    return privateKeyToAddress(s.agentKey as Hex).toLowerCase() === s.agentAddress.toLowerCase();
  } catch {
    return false;
  }
}

/** The stored session for (env, user), or null. Expired / malformed entries are removed. */
export function loadAgentSession(env: EnvName, user: string, now = Date.now()): AgentSession | null {
  const st = storage();
  if (!st) return null;
  const key = agentStorageKey(env, user);
  let parsed: unknown;
  try {
    const raw = st.getItem(key);
    if (!raw) return null;
    parsed = JSON.parse(raw);
  } catch {
    clearAgentSession(env, user);
    return null;
  }
  if (!valid(parsed, env, user) || parsed.validUntil <= now) {
    clearAgentSession(env, user);
    return null;
  }
  return parsed;
}

export function saveAgentSession(s: AgentSession): void {
  try {
    storage()?.setItem(agentStorageKey(s.env, s.user), JSON.stringify(s));
  } catch {
    /* ignore: session lives in memory only */
  }
}

export function clearAgentSession(env: EnvName, user: string): void {
  try {
    storage()?.removeItem(agentStorageKey(env, user));
  } catch {
    /* ignore */
  }
}

/** Remove the sessions of `user` on every env (address change in the wallet). */
export function clearAgentSessionsFor(user: string): void {
  clearAgentSession('testnet', user);
  clearAgentSession('mainnet', user);
}

/** Never print the key: a safe description of a session for logs / UI. */
export function describeSession(s: AgentSession): string {
  return `agent ${s.agentAddress} for ${s.user} on ${s.env}, valid until ${new Date(s.validUntil).toISOString()}`;
}
